import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SitesService } from 'src/app/sites/sites.service';
import { DeviceFactory } from '../../../device-factory/device-factory';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';

@Component({
	selector: 'app-battlink-utilization-report',
	templateUrl: './battlink-utilization-report.component.html',
	styleUrls: ['./battlink-utilization-report.component.css']
})
export class BattlinkUtilizationReportComponent implements OnInit, OnDestroy{
	@ViewChild('printModal') printModal: any;
	@ViewChild('hiddenPrintBtn') hiddenPrintBtn: ElementRef;

	siteId: number;
	data: any = {};
	devices: any = {};
	// data starts as an empty object, which is truthy, so *ngIf="data" never actually waited for the
	// response. The score sections below now show "no data to show", so they must not render until we
	// know what there is to show.
	dataLoaded = false;
	siteSub: Subscription = new Subscription();
	siteInfo!: string;
	isPrintModalOpen = false;
	totalDevices = [];

	siteInstallationDate: Date = new Date(0);
	toDate = new Date();
	fromDate = new Date(this.toDate.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago

	batteryVoltagesOptions: any[] = [];
	selectedBatteryVoltage: string = 'all';
	batteryCapacitiesOptions: any[] = [];
	selectedBatteryCapacity: string = 'all';
	manufacturersOptions: any[] = [];
	selectedManufacturer: string = 'all';

	batteryVoltagesOptionsForScores: any[] = [];
	selectedBatteryVoltageForScores: string = 'all';
	batteryCapacitiesOptionsForScores: any[] = [];
	selectedBatteryCapacityForScores: string = 'all';
	manufacturersOptionsForScores: any[] = [];
	selectedManufacturerForScores: string = 'all';

	periods = [
		{ value: 'last_7_days', label: this.translateService.instant('durations.last_7_days') },
		{ value: 'last_14_days', label: this.translateService.instant('durations.last_14_days') },
		{ value: 'last_30_days', label: this.translateService.instant('durations.last_30_days') },
		{ value: 'last_90_days', label: this.translateService.instant('durations.last_90_days') },
		{ value: 'year_to_date', label: this.translateService.instant('durations.year_to_date') },
		{ value: 'last_year', label: this.translateService.instant('durations.last_year') },
		{ value: 'since_installation', label: this.translateService.instant('durations.all_years') },
	];
	selectedPeriod = this.periods[0].value;
	period = this.periods[0].value;

	vintageOptionsDropdown = this.siteDashboardService.vintageOptionsDropdown;
	selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
	selectedBatteryVintageForScores = [this.siteDashboardService.vintageOptionsDropdown[0].value];

	constructor(
		private sitesService: SitesService,
		private route: ActivatedRoute,
		private sideMenuService: SideMenuService,
		private commonService: CommonService,
		private translateService: TranslateService,
		private siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		this.siteSub = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			this.siteInfo = siteInfo
			this.commonService.setPageTabTitle(`${siteInfo['customer_name']} - ${siteInfo['name']} - ${this.translateService.instant('g.battlink_site_utilization')}`);
		});
		this.siteId = this.route.parent.snapshot.params.siteId || 0;
		this.sitesService.getUtilizationScoreData(this.siteId, [DeviceFactory.types.battlink, DeviceFactory.types.battlinkII, DeviceFactory.types.lithiumBattlink]).subscribe((res: any) => {
			this.data = res.data;
			this.devices = res.devices;
			this.totalDevices = Object.values(res.devices);
			this.dataLoaded = true;
		});
	}

	onPeriodChanged(value: string) {
		this.period = value;
	}
	
	changeDate(field, value) {
		this[field] = value;
	}

	prepareParentFiltersOptions(event: { batteryVoltagesOptions: any[], batteryCapacitiesOptions: any[], manufacturers: any[] }, forScores: boolean = false) {
		setTimeout(() => {
			if (forScores) {
				const voltages = [...this.batteryVoltagesOptionsForScores, ...(event?.batteryVoltagesOptions || [])];
				const capacities = [...this.batteryCapacitiesOptionsForScores, ...(event?.batteryCapacitiesOptions || [])];
				const manufacturers = [...this.manufacturersOptionsForScores, ...(event?.manufacturers || [])];

				this.batteryVoltagesOptionsForScores = [...new Map(voltages.map(i => [i.value, i])).values()];
				this.batteryCapacitiesOptionsForScores = [...new Map(capacities.map(i => [i.value, i])).values()];
				this.manufacturersOptionsForScores = [...new Map(manufacturers.map(i => [i.value, i])).values()];
			} else {
				const voltages = [...this.batteryVoltagesOptions, ...(event?.batteryVoltagesOptions || [])];
				const capacities = [...this.batteryCapacitiesOptions, ...(event?.batteryCapacitiesOptions || [])];
				const manufacturers = [...this.manufacturersOptions, ...(event?.manufacturers || [])];

				this.batteryVoltagesOptions = [...new Map(voltages.map(i => [i.value, i])).values()];
				this.batteryCapacitiesOptions = [...new Map(capacities.map(i => [i.value, i])).values()];
				this.manufacturersOptions = [...new Map(manufacturers.map(i => [(i.value || '').toLowerCase(), i])).values()];
			}
		});
	}

	batteryVintageSelectionChanged() {
		if (!this.selectedBatteryVintage.length)
			this.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];

		if (!this.selectedBatteryVintageForScores.length)
			this.selectedBatteryVintageForScores = [this.siteDashboardService.vintageOptionsDropdown[0].value];

		if (this.selectedBatteryVintage.length > 1 && this.selectedBatteryVintage.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			if (this.selectedBatteryVintage[0] == this.siteDashboardService.vintageOptionsDropdown[0].value)
				this.selectedBatteryVintage = this.selectedBatteryVintage.filter(value => value !== this.siteDashboardService.vintageOptionsDropdown[0].value);
			else
				this.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
		}

		if (this.selectedBatteryVintageForScores.length > 1 && this.selectedBatteryVintageForScores.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			if (this.selectedBatteryVintageForScores[0] == this.siteDashboardService.vintageOptionsDropdown[0].value)
				this.selectedBatteryVintageForScores = this.selectedBatteryVintageForScores.filter(value => value !== this.siteDashboardService.vintageOptionsDropdown[0].value);
			else
				this.selectedBatteryVintageForScores = [this.siteDashboardService.vintageOptionsDropdown[0].value];
		}
	}

	ngOnDestroy() {
		if (this.siteSub)
			this.siteSub.unsubscribe();
	}

	openPrintModal() {
		this.printModal.show();
		this.isPrintModalOpen = true;
	}

	confirmPrint() {
		this.period = this.period;
		this.isPrintModalOpen = false;
		this.printModal.hide();
		setTimeout(() => {
			this.hiddenPrintBtn.nativeElement.click();
		}, 200);
	}
}
